"use client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Heart,
  LogOut,
  ShoppingBag,
  Tally4,
  User,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { getInitials } from "@/lib/getInitials";
import { trimText } from "@/lib/trimText";
import { signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";

export default function ProfileMenu() {
  const router = useRouter();
  const { data: session } = useSession();
  const { success } = useToast();

  const handleLogout = (e) => {
    e.preventDefault();
    signOut({ redirect: false, callbackUrl: "/" });
    router.push("/");
    success("You are logged out");
  };

  const menuItems = [
    { icon: User, label: "Account", href: "/account" },
    { icon: ShoppingBag, label: "Orders", href: "/orders" },
    { icon: Heart, label: "Wishlists", href: "/wishlist" },
    { icon: Tally4, label: "Points", href: "/points" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="cursor-pointer ring-2 ring-brand-sky/30 hover:ring-brand-sky transition-all duration-200">
          <AvatarImage
            src={session?.user?.image || "https://github.com/shadcn.png"}
            alt="profile image"
          />
          <AvatarFallback className="gradient-sky text-white font-semibold">
            {getInitials(session?.user?.name)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 p-2 rounded-2xl shadow-brand-lg border border-brand-light">
        <DropdownMenuLabel className="p-3 bg-brand-light/50 rounded-xl">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 ring-2 ring-white shadow-brand-sm">
              <AvatarImage
                src={session?.user?.image || "https://github.com/shadcn.png"}
                alt="profile image"
              />
              <AvatarFallback className="gradient-sky text-white text-xs font-semibold">
                {getInitials(session?.user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-navy truncate capitalize">
                {trimText(session?.user?.name, 25) || "User"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {trimText(session?.user?.email, 30)}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1 bg-brand-light" />
        <DropdownMenuGroup>
          {menuItems.map((item) => (
            <DropdownMenuItem key={item.href} className="p-0 focus:bg-transparent">
              <Link
                href={item.href}
                className="group flex items-center w-full px-3 py-2.5 rounded-lg hover:bg-brand-light/60 transition-colors duration-200"
              >
                <item.icon className="mr-3 h-4 w-4 text-brand-deep group-hover:text-brand-sky transition-colors" />
                <span className="text-sm font-medium text-foreground group-hover:text-brand-navy transition-colors">
                  {item.label}
                </span>
                <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground group-hover:text-brand-sky group-hover:translate-x-0.5 transition-all" />
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="my-1 bg-brand-light" />
        <DropdownMenuItem className="p-0 focus:bg-transparent">
          <button
            onClick={handleLogout}
            className="group flex items-center w-full px-3 py-2.5 rounded-lg hover:bg-red-500/10 transition-colors duration-200"
          >
            <LogOut className="mr-3 h-4 w-4 text-red-500 group-hover:scale-110 transition-transform" />
            <span className="text-sm font-medium text-red-500">
              Log out
            </span>
            <ChevronRight className="ml-auto h-4 w-4 text-red-500/50 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all" />
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}